import {
  mysqlTable,
  int,
  bigint,
  tinyint,
  smallint,
  varchar,
  text,
  decimal,
  double,
  date,
  time,
  datetime,
  boolean,
  timestamp,
  mysqlEnum,
  index,
  unique,
} from "drizzle-orm/mysql-core";
import { companies, companyAddresses } from "./tenancy";
import { users } from "./identity";
import { leaveTypes } from "./leave";

/**
 * Batch 1.3 — Attendance & shifts (reskin-docs/05). Time columns store UTC; late/half-day
 * comparisons happen in company tz. Ported line-by-line:
 *   base migration: employee_shifts L219–233, employee_shift_schedules L931–946,
 *   employee_shift_change_requests L948–959, attendances L278–306.
 *   employee_shift_rotations (2024_06_25_043142), shift_rotation_sequences (2024_06_25_044434),
 *   automate_shifts (2024_06_26_105345).
 *   AttendancePolicy module (2026_04_22_0900xx): attendance_policies / _rules / _assignments.
 * Deferred FK: attendance_policy_rules.action_target_leave_type_id → leave_types (Batch 1.4).
 */

export const employeeShifts = mysqlTable("employee_shifts", {
  id: bigint("id", { mode: "number", unsigned: true }).autoincrement().primaryKey(),
  companyId: int("company_id", { unsigned: true }).references(() => companies.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  shiftName: varchar("shift_name", { length: 255 }).notNull(),
  shiftShortCode: varchar("shift_short_code", { length: 255 }).notNull(),
  shiftType: mysqlEnum("shift_type", ["strict", "flexible"]).default("strict").notNull(),
  flexibleHalfDayHours: double("flexible_half_day_hours"),
  flexibleTotalHours: double("flexible_total_hours"),
  flexibleAutoClockout: double("flexible_auto_clockout"),
  color: varchar("color", { length: 255 }).notNull(),
  officeStartTime: time("office_start_time").notNull(),
  officeEndTime: time("office_end_time").notNull(),
  autoClockOutTime: int("auto_clock_out_time").default(1).notNull(),
  earlyClockIn: varchar("early_clock_in", { length: 255 }),
  halfdayMarkTime: time("halfday_mark_time"),
  lateMarkDuration: tinyint("late_mark_duration").notNull(),
  clockinInDay: tinyint("clockin_in_day").notNull(),
  officeOpenDays: text("office_open_days").notNull(),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

export const employeeShiftRotations = mysqlTable("employee_shift_rotations", {
  id: int("id", { unsigned: true }).autoincrement().primaryKey(),
  companyId: int("company_id", { unsigned: true }).references(() => companies.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  rotationName: varchar("rotation_name", { length: 255 }),
  rotationFrequency: varchar("rotation_frequency", { length: 255 }),
  scheduleOn: varchar("schedule_on", { length: 255 }),
  rotationDate: int("rotation_date"),
  colorCode: varchar("color_code", { length: 255 }),
  overrideShift: mysqlEnum("override_shift", ["yes", "no"]).default("no").notNull(),
  status: mysqlEnum("status", ["active", "inactive"]).default("active").notNull(),
  sendMail: mysqlEnum("send_mail", ["yes", "no"]).default("yes").notNull(),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

/** shift_rotation_sequences — has NO company_id (legacy). */
export const shiftRotationSequences = mysqlTable("shift_rotation_sequences", {
  id: int("id", { unsigned: true }).autoincrement().primaryKey(),
  employeeShiftRotationId: int("employee_shift_rotation_id", { unsigned: true }).references(
    () => employeeShiftRotations.id,
    { onDelete: "cascade", onUpdate: "cascade" },
  ),
  employeeShiftId: bigint("employee_shift_id", { mode: "number", unsigned: true }).references(
    () => employeeShifts.id,
    { onDelete: "cascade", onUpdate: "cascade" },
  ),
  sequence: int("sequence"),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

/** automate_shifts — has NO company_id (legacy). */
export const automateShifts = mysqlTable("automate_shifts", {
  id: bigint("id", { mode: "number", unsigned: true }).autoincrement().primaryKey(),
  userId: int("user_id", { unsigned: true })
    .notNull()
    .references(() => users.id, { onDelete: "cascade", onUpdate: "cascade" }),
  employeeShiftRotationId: int("employee_shift_rotation_id", { unsigned: true }).references(
    () => employeeShiftRotations.id,
    { onDelete: "cascade", onUpdate: "cascade" },
  ),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

/** employee_shift_schedules — has NO company_id (legacy). */
export const employeeShiftSchedules = mysqlTable(
  "employee_shift_schedules",
  {
    id: bigint("id", { mode: "number", unsigned: true }).autoincrement().primaryKey(),
    userId: int("user_id", { unsigned: true })
      .notNull()
      .references(() => users.id, { onDelete: "cascade", onUpdate: "cascade" }),
    companyId: int("company_id", { unsigned: true }).references(() => companies.id, {
      onDelete: "cascade",
      onUpdate: "cascade",
    }),
    date: date("date").notNull(),
    employeeShiftId: bigint("employee_shift_id", { mode: "number", unsigned: true })
      .notNull()
      .references(() => employeeShifts.id, { onDelete: "cascade", onUpdate: "cascade" }),
    addedBy: int("added_by", { unsigned: true }).references(() => users.id, {
      onDelete: "set null",
      onUpdate: "cascade",
    }),
    lastUpdatedBy: int("last_updated_by", { unsigned: true }).references(() => users.id, {
      onDelete: "set null",
      onUpdate: "cascade",
    }),
    shiftStartTime: datetime("shift_start_time"),
    shiftEndTime: datetime("shift_end_time"),
    remarks: text("remarks"),
    file: varchar("file", { length: 255 }).notNull(),
    createdAt: timestamp("created_at"),
    updatedAt: timestamp("updated_at"),
  },
  (t) => [index("employee_shift_schedules_date_index").on(t.date)],
);

export const employeeShiftChangeRequests = mysqlTable("employee_shift_change_requests", {
  id: bigint("id", { mode: "number", unsigned: true }).autoincrement().primaryKey(),
  companyId: int("company_id", { unsigned: true }).references(() => companies.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  shiftScheduleId: bigint("shift_schedule_id", { mode: "number", unsigned: true })
    .notNull()
    .references(() => employeeShiftSchedules.id, { onDelete: "cascade", onUpdate: "cascade" }),
  employeeShiftId: bigint("employee_shift_id", { mode: "number", unsigned: true })
    .notNull()
    .references(() => employeeShifts.id, { onDelete: "cascade", onUpdate: "cascade" }),
  status: mysqlEnum("status", ["waiting", "accepted", "rejected"]).default("waiting").notNull(),
  reason: text("reason"),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

export const attendances = mysqlTable(
  "attendances",
  {
    id: int("id", { unsigned: true }).autoincrement().primaryKey(),
    companyId: int("company_id", { unsigned: true }).references(() => companies.id, {
      onDelete: "cascade",
      onUpdate: "cascade",
    }),
    userId: int("user_id", { unsigned: true })
      .notNull()
      .references(() => users.id, { onDelete: "cascade", onUpdate: "cascade" }),
    locationId: bigint("location_id", { mode: "number", unsigned: true }).references(
      () => companyAddresses.id,
      { onDelete: "set null", onUpdate: "cascade" },
    ),
    clockInTime: datetime("clock_in_time").notNull(),
    clockOutTime: datetime("clock_out_time"),
    autoClockOut: boolean("auto_clock_out").default(false).notNull(),
    clockInType: varchar("clock_in_type", { length: 255 }),
    clockOutType: varchar("clock_out_type", { length: 255 }),
    clockInIp: varchar("clock_in_ip", { length: 255 }).notNull(),
    clockOutIp: varchar("clock_out_ip", { length: 255 }),
    workingFrom: varchar("working_from", { length: 255 }).default("office"),
    workFromType: mysqlEnum("work_from_type", ["home", "office", "other"]).default("other"),
    overwriteAttendance: mysqlEnum("overwrite_attendance", ["yes", "no"]).default("no"),
    late: mysqlEnum("late", ["yes", "no"]).default("no").notNull(),
    halfDay: mysqlEnum("half_day", ["yes", "no"]).notNull(),
    halfDayType: varchar("half_day_type", { length: 255 }),
    addedBy: int("added_by", { unsigned: true }).references(() => users.id, {
      onDelete: "set null",
      onUpdate: "cascade",
    }),
    lastUpdatedBy: int("last_updated_by", { unsigned: true }).references(() => users.id, {
      onDelete: "set null",
      onUpdate: "cascade",
    }),
    latitude: decimal("latitude", { precision: 10, scale: 8 }),
    longitude: decimal("longitude", { precision: 11, scale: 8 }),
    shiftStartTime: datetime("shift_start_time"),
    shiftEndTime: datetime("shift_end_time"),
    employeeShiftId: bigint("employee_shift_id", { mode: "number", unsigned: true }).references(
      () => employeeShifts.id,
      { onDelete: "set null", onUpdate: "cascade" },
    ),
    createdAt: timestamp("created_at"),
    updatedAt: timestamp("updated_at"),
  },
  (t) => [
    index("attendances_clock_in_time_index").on(t.clockInTime),
    index("attendances_clock_out_time_index").on(t.clockOutTime),
  ],
);

export const attendancePolicies = mysqlTable(
  "attendance_policies",
  {
    id: int("id", { unsigned: true }).autoincrement().primaryKey(),
    companyId: int("company_id", { unsigned: true })
      .notNull()
      .references(() => companies.id, { onDelete: "cascade", onUpdate: "cascade" }),
    name: varchar("name", { length: 255 }).notNull(),
    description: text("description"),
    effectiveFrom: date("effective_from").notNull(),
    effectiveTo: date("effective_to"),
    graceMinutes: smallint("grace_minutes", { unsigned: true }).default(0).notNull(),
    halfDayAfterMinutes: smallint("half_day_after_minutes", { unsigned: true }).default(0).notNull(),
    absentAfterMinutes: smallint("absent_after_minutes", { unsigned: true }).default(0).notNull(),
    isActive: boolean("is_active").default(true).notNull(),
    createdBy: int("created_by", { unsigned: true }).references(() => users.id, {
      onDelete: "set null",
      onUpdate: "cascade",
    }),
    createdAt: timestamp("created_at"),
    updatedAt: timestamp("updated_at"),
    deletedAt: timestamp("deleted_at"),
  },
  (t) => [
    index("ap_active_idx").on(t.companyId, t.isActive),
    index("ap_effective_idx").on(t.companyId, t.effectiveFrom, t.effectiveTo),
  ],
);

export const attendancePolicyRules = mysqlTable(
  "attendance_policy_rules",
  {
    id: int("id", { unsigned: true }).autoincrement().primaryKey(),
    companyId: int("company_id", { unsigned: true })
      .notNull()
      .references(() => companies.id, { onDelete: "cascade", onUpdate: "cascade" }),
    policyId: int("policy_id", { unsigned: true })
      .notNull()
      .references(() => attendancePolicies.id, { onDelete: "cascade", onUpdate: "cascade" }),
    order: smallint("order", { unsigned: true }).default(0).notNull(),
    conditionType: mysqlEnum("condition_type", [
      "minutes_late",
      "cumulative_late_days",
      "early_leave_minutes",
      "short_hours",
      "absent",
    ]).notNull(),
    conditionOperator: mysqlEnum("condition_operator", ["gte", "lte", "eq", "between"]).notNull(),
    conditionValueA: decimal("condition_value_a", { precision: 10, scale: 2 }).notNull(),
    conditionValueB: decimal("condition_value_b", { precision: 10, scale: 2 }),
    periodScope: mysqlEnum("period_scope", ["day", "week", "month", "pay_cycle"])
      .default("month")
      .notNull(),
    actionType: mysqlEnum("action_type", [
      "mark_late",
      "mark_half_day",
      "mark_absent",
      "deduct_leave",
      "penalty_amount",
      "warning",
    ]).notNull(),
    actionValue: decimal("action_value", { precision: 12, scale: 2 }),
    actionTargetLeaveTypeId: int("action_target_leave_type_id", { unsigned: true }).references(
      () => leaveTypes.id,
      { onDelete: "set null", onUpdate: "cascade" },
    ),
    createdAt: timestamp("created_at"),
    updatedAt: timestamp("updated_at"),
  },
  (t) => [index("apr_policy_order_idx").on(t.policyId, t.order)],
);

export const attendancePolicyAssignments = mysqlTable(
  "attendance_policy_assignments",
  {
    id: int("id", { unsigned: true }).autoincrement().primaryKey(),
    companyId: int("company_id", { unsigned: true })
      .notNull()
      .references(() => companies.id, { onDelete: "cascade", onUpdate: "cascade" }),
    policyId: int("policy_id", { unsigned: true })
      .notNull()
      .references(() => attendancePolicies.id, { onDelete: "cascade", onUpdate: "cascade" }),
    targetType: mysqlEnum("target_type", [
      "company",
      "shift",
      "team",
      "designation",
      "employee",
    ]).notNull(),
    targetId: bigint("target_id", { mode: "number", unsigned: true }), // polymorphic — no FK (legacy)
    priority: smallint("priority", { unsigned: true }).default(100).notNull(),
    effectiveFrom: date("effective_from").notNull(),
    effectiveTo: date("effective_to"),
    createdAt: timestamp("created_at"),
    updatedAt: timestamp("updated_at"),
  },
  (t) => [
    unique("apa_unique_assignment").on(
      t.companyId,
      t.policyId,
      t.targetType,
      t.targetId,
      t.effectiveFrom,
    ),
    index("apa_target_idx").on(t.companyId, t.targetType, t.targetId),
  ],
);

/**
 * wfh_requests — work-from-home requests (legacy Modules/BiometricSync; migration 2026_05_12_120000).
 * NET-NEW to our schema (B3 follow-up). One row per WFH date; approval flow (pending→approved/rejected/
 * cancelled). Approved WFH lets the employee work remotely that day.
 */
export const wfhRequests = mysqlTable(
  "wfh_requests",
  {
    id: bigint("id", { mode: "number", unsigned: true }).autoincrement().primaryKey(),
    companyId: int("company_id", { unsigned: true }).notNull().references(() => companies.id, { onDelete: "cascade", onUpdate: "cascade" }),
    userId: int("user_id", { unsigned: true }).notNull().references(() => users.id, { onDelete: "cascade", onUpdate: "cascade" }),
    wfhDate: date("wfh_date").notNull(),
    reason: text("reason"),
    status: mysqlEnum("status", ["pending", "approved", "rejected", "cancelled"]).default("pending").notNull(),
    submittedBy: int("submitted_by", { unsigned: true }).notNull(),
    submittedAt: timestamp("submitted_at").defaultNow(),
    decidedBy: int("decided_by", { unsigned: true }),
    decidedAt: timestamp("decided_at"),
    decisionNote: text("decision_note"),
    createdAt: timestamp("created_at"),
    updatedAt: timestamp("updated_at"),
  },
  (t) => [
    index("wfh_company_status").on(t.companyId, t.status),
    index("wfh_user_date").on(t.userId, t.wfhDate),
  ],
);

/**
 * attendance_corrections — punch correction / regularization requests (legacy Modules/BiometricSync;
 * migration 2026_05_12_100000). NET-NEW to our schema. On approval we upsert an `attendances` row from
 * requested_in/out (our model stores attendance in `attendances`, not biometric_punches).
 */
export const attendanceCorrections = mysqlTable(
  "attendance_corrections",
  {
    id: bigint("id", { mode: "number", unsigned: true }).autoincrement().primaryKey(),
    companyId: int("company_id", { unsigned: true }).notNull().references(() => companies.id, { onDelete: "cascade", onUpdate: "cascade" }),
    userId: int("user_id", { unsigned: true }).notNull().references(() => users.id, { onDelete: "cascade", onUpdate: "cascade" }),
    correctionDate: date("correction_date").notNull(),
    requestedIn: time("requested_in"),
    requestedOut: time("requested_out"),
    reasonCode: varchar("reason_code", { length: 32 }).notNull(),
    note: text("note"),
    status: mysqlEnum("status", ["pending", "approved", "rejected", "cancelled"]).default("pending").notNull(),
    submittedBy: int("submitted_by", { unsigned: true }).notNull(),
    submittedAt: timestamp("submitted_at").defaultNow(),
    decidedBy: int("decided_by", { unsigned: true }),
    decidedAt: timestamp("decided_at"),
    decisionNote: text("decision_note"),
    appliedAttendanceId: int("applied_attendance_id", { unsigned: true }),
    createdAt: timestamp("created_at"),
    updatedAt: timestamp("updated_at"),
  },
  (t) => [
    index("corrections_company_status").on(t.companyId, t.status),
    index("corrections_user_date").on(t.userId, t.correctionDate),
  ],
);
